import { useAtomValue } from 'jotai';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { currentFrameAtom } from '../../../labelling-v2/atoms/video-playback-atoms';
import type { SAMSegmentVideoData } from '../../../types';
import { currentFrameAnnotationBitmapsAtom } from '../../components/annotations/atoms/segmentation-atoms';
import { imageDataToBase64String, invalidateOutlineCache, mergeBitmaps } from '../../components/annotations/canvas/utils';
import { MAX_FRAMES_PER_TRACKING_CALL } from '../../constants';
import type { MetaDataT } from '../../reducers/actionReducers/annotations/annotations';
import type { AnnotationT, LabelStateT } from '../../types';
import { processFramesInChunks } from '../../utils';
import { processSAMTrackingChunk } from '../utils/tracking';

/*
   Hook to handle updating annotation bitmaps and review bitmaps cache when accepting or rejecting SAM annotations
*/
export const useSegmentationBitmapUpdater = () => {
  const annotationBitmap = useAtomValue(currentFrameAnnotationBitmapsAtom);
  const currentFrame = useAtomValue(currentFrameAtom);
  const updateAnnotationBitmap = async (state: LabelStateT, metaData: {
    frameIndex: number;
    annotationId: string;
  }) => {
    if (!metaData?.annotationId || metaData.frameIndex === undefined) return;
    // Bitmap is now derived from annotation store — no manual update needed.
  };
  const deleteAnnotationFrameSpan = (_state: LabelStateT, _metaData: MetaDataT) => {
    // Bitmap is now derived from annotation store — deletion is reflected automatically.
  };
  const deleteAllAnnotationBitmaps = (_state: LabelStateT) => {
    // Bitmap is now derived from annotation store — deletion is reflected automatically.
  };
  const deleteAllLabelBitmaps = (_state: LabelStateT, _metaData: MetaDataT) => {
    // Bitmap is now derived from annotation store — deletion is reflected automatically.
  };
  const updateBitmapOnAccept = (annotationLabel: AnnotationLabelT, frameIndex: number, reviewBitmaps: Record<string, ImageData>) => {
    const reviewBitmap = reviewBitmaps[annotationLabel.title];
    if (!reviewBitmap) {
      console.error('No review bitmap found for the annotation');
      return null;
    }
    const fillColor = annotationLabel.style?.fillColor || annotationLabel.style?.strokeColor;
    if (!fillColor) {
      console.error('No fill or stroke color available for the annotation');
      return null;
    }
    if (frameIndex !== currentFrame) {
      return imageDataToBase64String(reviewBitmap);
    }
    const existingBitmap = annotationBitmap?.[annotationLabel.title];
    invalidateOutlineCache(annotationLabel.title);
    if (!existingBitmap) {
      return imageDataToBase64String(reviewBitmap);
    }
    const {
      mergedBitmap
    } = mergeBitmaps(existingBitmap, reviewBitmap, fillColor);
    return imageDataToBase64String(mergedBitmap);
  };
  const updateBitmapsOnTrack = async (samTrackingData: SAMSegmentVideoData, annotationLabel: AnnotationLabelT, startFrame = 0, sourceAnnotation?: AnnotationT) => {
    let updatedAnnotationsArray: {
      [key: number]: AnnotationT;
    } = {};
    let allBitmapUpdates: {
      [frameIndex: number]: ImageData;
    } = {};
    const bitmapForHelpers: {
      [frameIndex: number]: {
        [label: string]: ImageData;
      };
    } = {
      [currentFrame]: annotationBitmap
    };
    const chunkResults = await processFramesInChunks<{
      bitmapUpdates: {
        [frameIndex: number]: ImageData;
      };
      annotations: {
        [frameIndex: number]: AnnotationT;
      };
    }>(0, samTrackingData.length - 1, MAX_FRAMES_PER_TRACKING_CALL, async (chunkStart, chunkEnd) => {
      const chunk = samTrackingData.slice(chunkStart, chunkEnd + 1);
      return processSAMTrackingChunk(chunk, annotationLabel, startFrame, chunkStart, bitmapForHelpers, sourceAnnotation);
    });
    chunkResults.forEach(chunkResult => {
      if (chunkResult) {
        allBitmapUpdates = {
          ...allBitmapUpdates,
          ...chunkResult.bitmapUpdates
        };
        updatedAnnotationsArray = {
          ...updatedAnnotationsArray,
          ...chunkResult.annotations
        };
      }
    });
    const currentFrameBitmap = allBitmapUpdates[currentFrame];
    if (currentFrameBitmap) {
      invalidateOutlineCache(annotationLabel.title);
    }
    return updatedAnnotationsArray;
  };
  const removeReviewBitmapOnReject = (_labelTitle: string) => {
    // Clears automatically via derived annotationsUnderReviewBitmapsAtom
    // when the annotation is removed from annotationUnderUserReviewAtom.
  };
  return {
    updateBitmapOnAccept,
    removeReviewBitmapOnReject,
    updateBitmapsOnTrack,
    updateAnnotationBitmap,
    deleteAnnotationFrameSpan,
    deleteAllAnnotationBitmaps,
    deleteAllLabelBitmaps
  };
};